import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { track } from '../lib/amplitude';
import { AppLink } from '../components/Nav';
import { useRecorderOverlay } from '../recorder/RecorderOverlay';
import { DateFilter } from '../components/DateFilter';
import { TagFilter } from '../components/TagFilter';
import { VideoGrid, VideoGridSkeleton } from '../components/VideoCard';
import { ChevronDownIcon, DownloadIcon, LinkIcon, RecordIcon, SearchIcon } from '../components/icons';
import { Banner, Button, EmptyState, ErrorState, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { dateBounds, type DateRange } from '../lib/dateRange';
import type { VideoSummary } from '../lib/types';
import { fetchMe } from '../queries/account';
import { orgSharesLibrary, subscriptionNotice } from '../queries/orgs';
import {
  canManageVideo,
  deleteVideo,
  fetchLibrary,
  libraryScopeKey,
  restoreVideo,
  type LibraryQuery,
  type LibrarySort,
} from '../queries/videos';
import { qk } from '../queries/keys';

/**
 * The library: everything you recorded and, if you are on a team,
 * everything the team recorded, with a switch to just yours. Search covers
 * titles and transcripts, the date filter narrows by upload date, and the
 * archive tab holds deleted recordings for thirty days.
 *
 * Deleting is the way into that archive. It is offered on the recordings this
 * person may manage (their own, and anyone's if they run the team) and takes
 * two presses, since the first one only asks.
 */

type Tab = 'videos' | 'archive';

const SORT_OPTIONS: { value: LibrarySort; label: string }[] = [
  { value: 'newest', label: 'Newest to Oldest' },
  { value: 'oldest', label: 'Oldest to Newest' },
  { value: 'views', label: 'Most viewed' },
];

export function DashboardPage() {
  const { org, loading: orgsLoading } = useOrg();

  useEffect(() => {
    track('Viewed Library');
  }, []);
  const { open: openRecorder } = useRecorderOverlay();
  const queryClient = useQueryClient();
  // Your own recordings first; the team's are one switch away. Most visits
  // are to find something you made.
  const [mine, setMine] = useState(true);
  const [tab, setTab] = useState<Tab>('videos');
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [sort, setSort] = useState<LibrarySort>('newest');
  const [tagIds, setTagIds] = useState<string[]>([]);

  // Debounce typing so every keystroke does not hit the transcript search.
  useEffect(() => {
    const handle = window.setTimeout(() => setTerm(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  // A team whose plan ended no longer shares a library: what is listed here is
  // this person's own work, so the team's name and the whose-recordings filter
  // would both be describing something that is no longer true.
  const shared = org !== null && orgSharesLibrary(org.subscriptionStatus);

  const libraryQuery: LibraryQuery = {
    mine: shared && mine,
    limit: 24,
    q: term || undefined,
    ...dateBounds(dateRange),
    sort,
    archived: tab === 'archive',
    tags: tagIds,
  };

  const query = useInfiniteQuery({
    queryKey: qk.videos(libraryScopeKey(libraryQuery)),
    queryFn: ({ pageParam }) => fetchLibrary({ ...libraryQuery, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 20_000,
    placeholderData: (previous) => previous,
  });

  const invalidateLibrary = () => queryClient.invalidateQueries({ queryKey: ['videos'] });

  const restore = useMutation({
    mutationFn: (video: VideoSummary) => restoreVideo(video.id),
    onSuccess: (_result, video) => {
      track('Video Restored', { video_id: video.shareId });
      return invalidateLibrary();
    },
  });

  const remove = useMutation({
    mutationFn: (video: VideoSummary) => deleteVideo(video.id),
    onSuccess: (_result, video) => {
      track('Video Deleted', { video_id: video.shareId });
      // The same keys the restore invalidates: the card leaves Videos and
      // turns up in Archive without either tab being reloaded by hand.
      return invalidateLibrary();
    },
  });

  // Who is asking, so the delete only shows where the API would allow it.
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000 });
  const canDelete = (video: VideoSummary) =>
    canManageVideo(video, { userId: me.data?.id ?? null, org });

  const videos = query.data?.pages.flatMap((page) => page.videos) ?? [];
  const total = query.data?.pages[0]?.total ?? videos.length;
  const freeTier = query.data?.pages[0]?.freeTier ?? null;
  const filtered = term !== '' || dateRange !== null || tagIds.length > 0;
  const filterByTags = (next: string[]) => {
    setTagIds(next);
    track('Library Filtered', { filter: 'tag', count: next.length });
  };
  const notice = org ? subscriptionNotice(org.subscriptionStatus, org.trialEndsAt, 'library') : null;

  return (
    <>
      <label className="relative mb-8 block">
        <SearchIcon
          size={16}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-chalk-faint"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search your recordings"
          aria-label="Search titles and transcripts"
          className={cx(
            'h-11 w-full rounded-md border border-hairline bg-slate-900 pl-10 pr-4 text-body text-chalk',
            'placeholder:text-chalk-faint transition-colors duration-120 hover:border-slate-600',
            'focus:border-signal focus:outline-none',
          )}
        />
      </label>

      {/* Recording starts from the Record button in the header, which is on
          every page; a second one here said the same thing twice. */}
      <div className="mb-5">
        <p className="text-caption uppercase tracking-wide text-chalk-faint">
          {shared && org ? org.name : 'Library'}
        </p>
        <h1 className="mt-1 text-h1 font-semibold text-chalk">Videos</h1>
      </div>

      {notice ? (
        <div className="mb-5">
          <Banner tone={notice.tone} title={notice.title}>
            {notice.body}
          </Banner>
        </div>
      ) : null}

      {freeTier && freeTier.locked > 0 && tab === 'videos' ? (
        <div className="mb-5">
          <Banner
            tone="caution"
            title={`${freeTier.locked} ${freeTier.locked === 1 ? 'recording is' : 'recordings are'} locked`}
          >
            {freeTier.freeSeatOn ? (
              <>
                You are on {freeTier.freeSeatOn} with a free seat, so your own library is on the free
                plan: the {freeTier.limit} most recent recordings stay playable. Older ones stay here but
                cannot be watched until you make room, or until an owner of {freeTier.freeSeatOn} moves
                you to a paid seat.
              </>
            ) : (
              <>
                The free plan keeps your {freeTier.limit} most recent recordings playable. Older ones stay
                here but cannot be watched until you make room or{' '}
                <AppLink to="/settings/subscription" className="text-signal hover:underline">
                  start a team
                </AppLink>
                , which has no limit.
              </>
            )}
          </Banner>
        </div>
      ) : null}


      <div className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-hairline">
        <nav className="flex gap-6" aria-label="Library sections">
          {(
            [
              ['videos', 'Videos'],
              ['archive', 'Archive'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              aria-current={tab === value ? 'page' : undefined}
              className={cx(
                '-mb-px border-b-2 pb-3 text-body transition-colors duration-120',
                tab === value
                  ? 'border-chalk font-medium text-chalk'
                  : 'border-transparent text-chalk-dim hover:text-chalk',
              )}
            >
              {label}
            </button>
          ))}
        </nav>
        <span className="ml-auto pb-3 text-caption text-chalk-faint tnum">
          {query.isLoading ? '' : `${total} ${total === 1 ? 'video' : 'videos'}`}
        </span>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <DateFilter value={dateRange} onChange={setDateRange} />
        <TagFilter value={tagIds} onChange={filterByTags} />
        {shared && org && tab === 'videos' ? (
          <FilterSelect
            value={mine ? 'mine' : 'all'}
            onChange={(value) => {
              setMine(value === 'mine');
              track('Workspace Switched', { to: value === 'mine' ? 'personal' : 'team' });
            }}
            options={[
              { value: 'all', label: `Everyone on ${org.name}` },
              { value: 'mine', label: 'Just mine' },
            ]}
            active={mine}
            label="Whose recordings"
          />
        ) : null}
        <FilterSelect
          value={sort}
          onChange={(value) => setSort(value as LibrarySort)}
          options={SORT_OPTIONS}
          active={sort !== 'newest'}
          label="Sort order"
        />
      </div>

      {query.isLoading || orgsLoading ? <VideoGridSkeleton /> : null}

      {query.isError ? (
        <ErrorState
          title="Your library did not load"
          message={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {!query.isLoading && !query.isError && videos.length === 0 ? (
        tab === 'archive' ? (
          <EmptyState title={filtered ? 'Nothing archived matches' : 'The archive is empty'}>
            Deleted recordings wait here for thirty days before they are gone for good.
          </EmptyState>
        ) : filtered ? (
          <EmptyState title="No recordings match">
            Search looks through titles and transcripts. Try a different word, or widen the date
            range.
          </EmptyState>
        ) : mine && org ? (
          <EmptyState title="Nothing of yours yet">
            Everything you record shows here, and in {org.name}'s library with it.
          </EmptyState>
        ) : (
          <EmptyState
            title="No recordings yet"
            icon={<RecordIcon size={22} />}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={() => openRecorder()}>
                  Record something
                </Button>
                <AppLink to="/links">
                  <Button variant="ghost" className="gap-2">
                    <LinkIcon size={14} />
                    Ask someone else for one
                  </Button>
                </AppLink>
                <AppLink to="/import/loom">
                  <Button variant="ghost" className="gap-2">
                    <DownloadIcon size={14} />
                    Bring your Loom videos over
                  </Button>
                </AppLink>
              </div>
            }
          >
            Pick a screen, talk over it, send the link. It takes about ninety seconds to replace
            the meeting you were going to schedule.
          </EmptyState>
        )
      ) : null}

      {remove.isError ? (
        <div className="mb-5">
          <Banner tone="danger" title="That recording is still here">
            {errorMessage(remove.error)}
          </Banner>
        </div>
      ) : null}

      {videos.length > 0 ? (
        <VideoGrid
          videos={videos}
          className={cx(query.isPlaceholderData && 'opacity-60 transition-opacity')}
          onRestore={tab === 'archive' ? (video) => restore.mutate(video) : undefined}
          onDelete={tab === 'videos' ? (video) => remove.mutate(video) : undefined}
          onTag={(tagId) => {
            if (!tagIds.includes(tagId)) filterByTags([...tagIds, tagId]);
          }}
          canDelete={canDelete}
          deletingId={remove.isPending ? remove.variables.id : null}
        />
      ) : null}

      {query.hasNextPage ? (
        <div className="mt-6 flex justify-center">
          <Button onClick={() => void query.fetchNextPage()} busy={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** A pill-shaped native select, styled like Loom's filter chips. */
function FilterSelect({
  value,
  onChange,
  options,
  active,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  active: boolean;
  label: string;
}) {
  return (
    <span className="relative inline-flex">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className={cx(
          'h-8 cursor-pointer appearance-none rounded-full border pl-3.5 pr-8 text-caption transition-colors duration-120',
          'focus:border-signal focus:outline-none',
          active
            ? 'border-signal/60 bg-signal/10 text-chalk'
            : 'border-hairline bg-slate-850 text-chalk-dim hover:border-slate-600 hover:text-chalk',
        )}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        size={12}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-chalk-faint"
      />
    </span>
  );
}
